<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Lista de Exames</h4>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    class="btn btn-info me-2 float-right" 
                    @click="syncOrthancExams"
                    :disabled="syncing"
                  >
                    <i class="bx bx-sync" :class="{ 'bx-spin': syncing }"></i>
                    {{ syncing ? 'Sincronizando...' : 'Sincronizar' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Filtros -->
            <div class="row mb-3">
              <div class="col-md-3">
                <select v-model="filters.status" class="form-select" @change="onFilterChange">
                  <option value="">Todos os status</option>
                  <option value="PENDING">Pendente</option>
                  <option value="IN_PROGRESS">Em Andamento</option>
                  <option value="COMPLETED">Concluído</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>
              <div class="col-md-3">
                <select v-model="filters.exam_type" class="form-select" @change="onFilterChange">
                  <option value="">Todos os tipos</option>
                  <option value="XRAY">Raio-X</option>
                  <option value="CT">Tomografia</option>
                  <option value="MRI">Ressonância</option>
                  <option value="ULTRASOUND">Ultrassom</option>
                  <option value="MAMMOGRAPHY">Mamografia</option>
                </select>
              </div>
              <div class="col-md-3">
                <div class="position-relative">
                  <input
                    v-model="filters.cpf"
                    type="text"
                    class="form-control"
                    placeholder="CPF do paciente"
                    @input="formatCPF"
                    @keyup.enter="handleCPFSearch"
                    @blur="handleCPFSearch"
                    maxlength="14"
                  />
                  <small v-if="filters.cpf && filters.cpf.replace(/\D/g, '').length < 11" class="text-muted">
                    Digite CPF completo para busca específica
                  </small>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12 col-md-6">
                <div id="exams-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Mostrar&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;registros
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="exams-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Buscar:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      class="form-control form-control-sm ml-2"
                      placeholder="ID, descrição..."
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
              </div>
            </div>

            <div v-else class="table-responsive">
              <b-table
                :items="exams"
                :fields="fields"
                responsive="sm"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <template v-slot:cell(id)="row">
                  <router-link :to="`/exams/${row.value}`" class="text-primary">
                    {{ row.value }}
                  </router-link>
                </template>

                <template v-slot:cell(patient)="row">
                  <div v-if="row.item.patient">
                    <strong>{{ row.item.patient.name || 'Nome não informado' }}</strong>
                    <br>
                    <small class="text-muted">
                      <span v-if="row.item.patient.cpf">
                        CPF: {{ formatCPFDisplay(row.item.patient.cpf) }}
                      </span>
                      <span v-else class="text-warning">CPF não informado</span>
                    </small>
                    <br>
                    <small class="text-muted" v-if="row.item.patient.birth_date">
                      Nascimento: {{ formatDate(row.item.patient.birth_date) }}
                    </small>
                  </div>
                  <div v-else-if="row.item.patient_id">
                    <small class="text-muted">
                      <i class="bx bx-loader-alt bx-spin"></i> Carregando...
                    </small>
                  </div>
                  <div v-else>
                    <small class="text-warning">Paciente não vinculado</small>
                  </div>
                </template>

                <template v-slot:cell(exam_type)="row">
                  <span class="badge bg-soft-primary text-primary">
                    {{ getExamTypeLabel(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(status)="row">
                  <span :class="getStatusBadgeClass(row.value)">
                    {{ getStatusLabel(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(scheduled_date)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(description)="row">
                  <div class="text-truncate" style="max-width: 250px;" :title="row.value">
                    {{ row.value || 'Sem descrição' }}
                  </div>
                </template>

                <template v-slot:cell(action)="row">
                  <div class="d-flex gap-2">
                    <a
                      href="javascript:void(0);"
                      class="text-primary"
                      v-b-tooltip.hover
                      title="Visualizar"
                      @click="viewDicom(row.item)"
                    >
                      <i class="mdi mdi-eye font-size-18"></i>
                    </a>                    
                  </div>
                </template>
              </b-table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { examService } from "@/api/exams";
import { patientService } from "@/api/patients";
import { dicomService } from "@/api/dicom";

export default {
  page: {
    title: "Lista de Exames",
    meta: [{ name: "description", content: "Lista de exames do sistema" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Lista de Exames",
      items: [
        {
          text: "Exames",
          href: "/exams",
        },
        {
          text: "Lista",
          active: true,
        },
      ],
      exams: [],
      loading: false,
      syncing: false,
      patientCache: {}, // Cache para dados de pacientes
      filters: {
        status: "",
        exam_type: "",
        cpf: "",
      },
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      filter: null,
      filterOn: [],
      sortBy: "id",
      sortDesc: true,
      fields: [
        { key: "id", sortable: true, label: "ID" },
        { key: "patient", sortable: false, label: "Paciente" },
        { key: "exam_type", sortable: true, label: "Tipo" },
        { key: "scheduled_date", sortable: true, label: "Data Agendada" },
        { key: "status", sortable: true, label: "Status" },
        { key: "description", sortable: false, label: "Descrição" },
        { key: "action", label: "Ações" }
      ]
    };
  },
  async created() {
    // Sincroniza com Orthanc ao carregar a página
    await this.syncOrthancExams(false); // false = não mostrar toast de sucesso
    await this.loadExams();
  },
  watch: {
    // Watch for page changes
    currentPage() {
      this.loadExams();
    },
    // Watch for perPage changes
    perPage() {
      this.currentPage = 1; // Reset to first page
      this.loadExams();
    }
  },
  methods: {
    async loadExams() {
      this.loading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.perPage,
          limit: this.perPage,
          ...this.activeFilters,
        };
        
        const response = await examService.listExams(params);
        
        if (response.data && response.data.exams) {
          this.exams = response.data.exams;
          this.totalRows = response.data.count || 0;
        } else if (Array.isArray(response.data)) {
          this.exams = response.data;
          this.totalRows = response.data.length;
        } else {
          console.error("Resposta da API inválida:", response);
          this.exams = [];
          this.totalRows = 0;
        }

        // Buscar dados dos pacientes para cada exame
        await this.loadPatientData();
        
      } catch (error) {
        console.error("Erro ao carregar exames:", error);
        this.$toast.error("Erro ao carregar exames");
        this.exams = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },

    async loadPatientData() {
      // Obter IDs únicos de pacientes que ainda não estão no cache
      const patientIds = [...new Set(
        this.exams
          .filter(exam => exam.patient_id && !this.patientCache[exam.patient_id])
          .map(exam => exam.patient_id)
      )];

      if (patientIds.length === 0) {
        // Se todos os pacientes já estão no cache, apenas associar os dados
        this.associatePatientData();
        return;
      }

      // Buscar dados dos pacientes em paralelo
      const patientPromises = patientIds.map(async (patientId) => {
        try {
          const patientResponse = await patientService.getPatientById(patientId);
          // Adicionar ao cache
          this.patientCache[patientId] = patientResponse.data;
        } catch (error) {
          console.warn(`Erro ao buscar paciente ${patientId}:`, error);
          // Adicionar dados padrão ao cache
          this.patientCache[patientId] = {
            id: patientId,
            name: 'Paciente não encontrado',
            cpf: null
          };
        }
      });

      // Aguardar todas as promessas
      await Promise.all(patientPromises);

      // Associar dados dos pacientes aos exames
      this.associatePatientData();
    },

    associatePatientData() {
      this.exams.forEach(exam => {
        if (exam.patient_id && this.patientCache[exam.patient_id]) {
          exam.patient = this.patientCache[exam.patient_id];
        }
      });
    },

    async syncOrthancExams(showToast = true) {
      this.syncing = true;
      try {
        const response = await examService.syncOrthancExams(100);
        if (showToast && response.data.success) {
          const stats = response.data.statistics;
          this.$toast.success(
            `Sincronização concluída: ${stats.studies_processed} estudos processados, ` +
            `${stats.exams_created} exames criados, ${stats.exams_updated} exames atualizados`
          );
        }
        
        if (response.data.statistics?.errors?.length > 0) {
          console.warn("Erros na sincronização:", response.data.statistics.errors);
        }

        // Limpar cache de pacientes após sincronização para recarregar dados
        this.patientCache = {};
        
      } catch (error) {
        console.error("Erro na sincronização:", error);
        if (showToast) {
          this.$toast.error("Erro ao sincronizar com Orthanc");
        }
      } finally {
        this.syncing = false;
      }
    },

    clearPatientCache() {
      this.patientCache = {};
    },

    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Para busca local, ajustar o total de linhas
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    /**
     * Handle filter changes - reset to first page and reload
     */
    onFilterChange() {
      this.currentPage = 1;
      this.loadExams();
    },

    async searchByCPF() {
      if (!this.filters.cpf || this.filters.cpf.length < 11) {
        // Se CPF não estiver completo, fazer busca normal
        this.onFilterChange();
        return;
      }

      this.loading = true;
      try {
        // Remover formatação do CPF
        const cleanCPF = this.filters.cpf.replace(/\D/g, '');
        
        // Buscar paciente pelo CPF
        const patientResponse = await patientService.getPatientByCpf(cleanCPF);
        
        if (patientResponse.data) {
          // Se encontrou o paciente, filtrar exames por patient_id
          const params = {
            offset: 0,
            limit: this.perPage,
            patient_id: patientResponse.data.id,
            ...this.activeFilters
          };
          
          const response = await examService.listExams(params);
          
          if (response.data && response.data.exams) {
            this.exams = response.data.exams;
            this.totalRows = response.data.count || 0;
          } else if (Array.isArray(response.data)) {
            this.exams = response.data;
            this.totalRows = response.data.length;
          }
          
          // Adicionar dados do paciente encontrado ao cache
          this.patientCache[patientResponse.data.id] = patientResponse.data;
          
          // Associar dados dos pacientes
          await this.loadPatientData();
        } else {
          // Se não encontrou paciente com esse CPF
          this.exams = [];
          this.totalRows = 0;
          this.$toast.warning("Nenhum paciente encontrado com este CPF");
        }
        
      } catch (error) {
        console.error("Erro ao buscar por CPF:", error);
        // Fallback para busca normal
        this.onFilterChange();
      } finally {
        this.loading = false;
      }
    },

    formatCPF() {
      // Remove caracteres não numéricos
      let cpf = this.filters.cpf.replace(/\D/g, '');
      
      // Aplica formatação XXX.XXX.XXX-XX
      if (cpf.length <= 11) {
        cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2');
        cpf = cpf.replace(/(\d{3})(\d)/, '$1.$2');
        cpf = cpf.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      }
      
      this.filters.cpf = cpf;
    },

    handleCPFSearch() {
      const cleanCPF = this.filters.cpf.replace(/\D/g, '');
      
      if (cleanCPF.length === 11) {
        // CPF completo - usar busca específica
        this.searchByCPF();
      } else if (cleanCPF.length === 0) {
        // CPF vazio - limpar filtro
        this.onFilterChange();
      }
      // CPF incompleto - não fazer busca automática
    },

    formatCPFDisplay(cpf) {
      if (!cpf) return '';
      // Remove caracteres não numéricos
      const cleaned = cpf.replace(/\D/g, '');
      
      // Aplica formatação XXX.XXX.XXX-XX
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      return cpf;
    },

    viewExam(exam) {
      this.$router.push(`/exams/${exam.id}`);
    },

    editExam(exam) {
      this.$router.push(`/exams/${exam.id}/edit`);
    },

    hasStudyUID(exam) {
      return (exam.description && exam.description.includes('Study UID:')) ||
             (exam.result && exam.result.includes('Study UID:'));
    },

    async viewDicom(exam) {
      try {
        // Extrai Study UID da descrição ou resultado
        let studyUID = null;
        if (exam.description && exam.description.includes('Study UID:')) {
          studyUID = exam.description.match(/Study UID: ([^\s|]+)/)?.[1];
        } else if (exam.result && exam.result.includes('Study UID:')) {
          studyUID = exam.result.match(/Study UID: ([^\s|]+)/)?.[1];
        }
        
        if (!studyUID) {
          this.$toast.error("Study UID não encontrado para este exame");
          return;
        }

        // Buscar links do viewer
        const response = await dicomService.getViewerLink(studyUID);
        
        if (response.data && response.data.viewer_links && response.data.viewer_links.ohif_viewer) {
          let viewerUrl = response.data.viewer_links.ohif_viewer;
          
          // Obter token do usuário autenticado
          let token = this.$store.state.auth.currentUser?.access_token || 
                     localStorage.getItem('token') || 
                     localStorage.getItem('access_token') ||
                     sessionStorage.getItem('token');
          
          // Adicionar token à URL
          if (token) {
            const separator = viewerUrl.includes('?') ? '&' : '?';
            viewerUrl = `${viewerUrl}${separator}token=${token}`;
          }
          
          // Abrir em nova aba
          window.open(viewerUrl, '_blank');
        } else {
          this.$toast.error("Link do visualizador DICOM não encontrado");
        }
      } catch (error) {
        console.error("Erro ao obter link do visualizador DICOM:", error);
        this.$toast.error("Erro ao abrir visualizador DICOM");
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('pt-BR');
    },

    getStatusLabel(status) {
      const labels = {
        PENDING: 'Pendente',
        IN_PROGRESS: 'Em Andamento',
        COMPLETED: 'Concluído',
        CANCELLED: 'Cancelado',
      };
      return labels[status] || status;
    },

    getStatusBadgeClass(status) {
      const classes = {
        PENDING: 'badge bg-soft-warning text-warning',
        IN_PROGRESS: 'badge bg-soft-info text-info',
        COMPLETED: 'badge bg-soft-success text-success',
        CANCELLED: 'badge bg-soft-danger text-danger',
      };
      return classes[status] || 'badge bg-soft-secondary text-secondary';
    },

    getExamTypeLabel(examType) {
      const labels = {
        XRAY: 'Raio-X',
        CT: 'Tomografia',
        MRI: 'Ressonância',
        ULTRASOUND: 'Ultrassom',
        MAMMOGRAPHY: 'Mamografia',
      };
      return labels[examType] || examType;
    },
  },

  computed: {
    /**
     * Total no. of records (from API response)
     */
    rows() {
      return this.totalRows;
    },

    activeFilters() {
      const filters = {};
      if (this.filters.status) filters.status = this.filters.status;
      if (this.filters.exam_type) filters.exam_type = this.filters.exam_type;
      
      // Não incluir CPF nos filtros ativos se estiver fazendo busca específica
      // O CPF será tratado separadamente na busca por paciente
      
      return filters;
    },
  },
};
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Bootstrap Vue Badge Colors */
.badge.bg-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge.bg-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge.bg-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
}

.badge.bg-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge.bg-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
}

.badge.bg-soft-secondary {
  color: #74788d;
  background-color: rgba(116, 120, 141, 0.18);
}

/* Text colors for badges */
.text-warning {
  color: #f1b44c !important;
}

.text-info {
  color: #50a5f1 !important;
}

.text-success {
  color: #34c38f !important;
}

.text-danger {
  color: #f46a6a !important;
}

.text-primary {
  color: #556ee6 !important;
}

.text-secondary {
  color: #74788d !important;
}
</style>
